import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Actor, ActorType } from '@hummii/shared-types';

/**
 * Auth Store - Actor/Persona Architecture
 *
 * ⚠️ SECURITY: Tokens are NOT stored here - they're in HTTP-only cookies
 * This prevents XSS attacks from stealing tokens via JavaScript
 *
 * Actor Model:
 * - One User can have multiple Actors (personas)
 * - Each Actor has a type: CLIENT, WORKER, SUPPORT, STORE
 * - Only one Actor is active at a time (activeActor)
 * - Switching actors requires backend API call to generate new JWT
 */

interface User {
  id: string;
  email: string;
  name: string;
  isAdmin: boolean;
  isVerified: boolean;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  activeActor: Actor | null;
  availableActors: Actor[];
  _hasHydrated: boolean;

  // Actions
  login: (user: User, activeActor: Actor, availableActors: Actor[]) => void;
  logout: () => void;
  updateUser: (user: Partial<User>) => void;
  setActiveActor: (actor: Actor) => void;
  setAvailableActors: (actors: Actor[]) => void;
  setHasHydrated: (state: boolean) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      activeActor: null,
      availableActors: [],
      _hasHydrated: false,

      login: (user, activeActor, availableActors) => {
        set({
          user,
          isAuthenticated: true,
          activeActor,
          availableActors,
        });
      },

      logout: () =>
        set({
          user: null,
          isAuthenticated: false,
          activeActor: null,
          availableActors: [],
        }),

      updateUser: (userData) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...userData } : null,
        })),

      setActiveActor: (actor) => {
        set({ activeActor: actor });
      },

      setAvailableActors: (actors) => {
        set({ availableActors: actors });
      },

      setHasHydrated: (state) => {
        set({ _hasHydrated: state });
      },
    }),
    {
      name: 'auth-storage',
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
      },
      // Persist user data and active actor
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        activeActor: state.activeActor,
        availableActors: state.availableActors,
      }),
    }
  )
);

// Selectors for convenience
export const selectActiveActorType = (state: AuthState): ActorType | null =>
  state.activeActor?.type ?? null;

export const selectHasMultipleActors = (state: AuthState): boolean =>
  state.availableActors.length > 1;

export const selectCanSwitchToActor = (state: AuthState, actorId: string): boolean =>
  state.availableActors.some((actor) => actor.id === actorId && actor.isActive);
